import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, legacyAppUrl } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import {
  Alert,
  DataTable,
  Empty,
  Kpi,
  KpiGrid,
  Muted,
  PageHeader,
  Section,
  StatusBadge,
} from '@/components/ui'
import { fetchAlertas, resolverAlerta } from './api'

export function AlertasPage() {
  const { canEdit } = useAuth()
  const qc = useQueryClient()
  const [status, setStatus] = useState('ativo')
  const [msg, setMsg] = useState('')

  const alertas = useQuery({
    queryKey: ['analytics', 'alertas', status],
    queryFn: () => fetchAlertas({ status: status || undefined }),
  })

  const mut = useMutation({
    mutationFn: ({ id, acao }: { id: string; acao: 'resolver' | 'ignorar' | 'ler' }) =>
      resolverAlerta(id, acao),
    onSuccess: async () => {
      setMsg('Alerta atualizado.')
      await qc.invalidateQueries({ queryKey: ['analytics', 'alertas'] })
    },
    onError: (err) => {
      setMsg(err instanceof ApiError ? err.message : 'Falha ao atualizar alerta.')
    },
  })

  const lista = alertas.data?.dados || []

  return (
    <section className="hi-page">
      <PageHeader
        title="Alertas corporativos"
        subtitle="Alertas inteligentes via API Analytics. Ações de resolução disponíveis para RH."
        actions={
          <a className="btn btn-secondary" href={legacyAppUrl('#/alertas-corporativos')}>
            Legado
          </a>
        }
      />

      {msg ? <Alert tone="info">{msg}</Alert> : null}

      <KpiGrid>
        <Kpi label="Não lidos / ativos" value={String(alertas.data?.nao_lidos ?? lista.length)} />
        <Kpi label="Listados" value={String(lista.length)} />
      </KpiGrid>

      <form
        className="hi-filters"
        onSubmit={(e) => {
          e.preventDefault()
        }}
      >
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="ativo">Ativos</option>
          <option value="resolvido">Resolvidos</option>
          <option value="ignorado">Ignorados</option>
          <option value="">Todos</option>
        </select>
      </form>

      <Section title="Alertas" flush>
        {alertas.isLoading ? <Muted>Carregando…</Muted> : null}
        {alertas.isError ? (
          <Alert tone="danger">
            {alertas.error instanceof ApiError ? alertas.error.message : 'Falha ao listar.'}
          </Alert>
        ) : null}
        {!alertas.isLoading && lista.length === 0 ? <Empty>Nenhum alerta neste filtro.</Empty> : null}
        {lista.length > 0 ? (
          <DataTable>
            <thead>
              <tr>
                <th>Título</th>
                <th>Tipo</th>
                <th>Prioridade</th>
                <th>Status</th>
                {canEdit('alertas-corporativos') ? <th>Ações</th> : null}
              </tr>
            </thead>
            <tbody>
              {lista.map((a) => (
                <tr key={a.id}>
                  <td>
                    <strong>{a.titulo || 'Alerta'}</strong>
                    {a.mensagem || a.descricao ? (
                      <div className="hi-muted">{String(a.mensagem || a.descricao)}</div>
                    ) : null}
                  </td>
                  <td>{a.tipo_display || a.tipo || '—'}</td>
                  <td>{(a.prioridade || '—').toString().toUpperCase()}</td>
                  <td>
                    <StatusBadge
                      tone={
                        a.status === 'ativo'
                          ? 'pendente'
                          : a.status === 'resolvido'
                            ? 'ativo'
                            : 'inativo'
                      }
                    >
                      {a.status || '—'}
                    </StatusBadge>
                  </td>
                  {canEdit('alertas-corporativos') ? (
                    <td className="hi-actions">
                      {a.status === 'ativo' ? (
                        <>
                          <button
                            type="button"
                            className="btn btn-primary"
                            disabled={mut.isPending}
                            onClick={() => mut.mutate({ id: a.id, acao: 'resolver' })}
                          >
                            Resolver
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary"
                            disabled={mut.isPending}
                            onClick={() => mut.mutate({ id: a.id, acao: 'ignorar' })}
                          >
                            Ignorar
                          </button>
                        </>
                      ) : null}
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </DataTable>
        ) : null}
      </Section>
    </section>
  )
}
